import React, { useState, useEffect } from 'react';
import { BetaSignup, ExportField } from '../../types';
import { apiService } from '../../services/apiService';
import { ExportService } from '../../services/exportService';
import FilterControls from './FilterControls';
import BulkActions from './BulkActions';
import ExportControls from './ExportControls';
import SignupsTable from './SignupsTable';

interface BetaManagementProps {
  signups: BetaSignup[];
  onRefresh: () => void;
}

const BetaManagement: React.FC<BetaManagementProps> = ({ signups, onRefresh }) => {
  const [filteredSignups, setFilteredSignups] = useState<BetaSignup[]>(signups);
  const [selectedSignups, setSelectedSignups] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [exportFields, setExportFields] = useState<ExportField>({
    firstName: true,
    lastName: true,
    email: true,
    platform: true,
    experienceYears: false,
    timestamp: false,
    status: false,
    testflightEmail: false,
    notes: false
  });

  // Update filtered signups when signups change
  useEffect(() => {
    setFilteredSignups(signups);
  }, [signups]);

  const handleStatusFilter = (status: string) => {
    setStatusFilter(status);
    applyFilters(status, searchTerm);
  };

  const handleSearch = (search: string) => {
    setSearchTerm(search);
    applyFilters(statusFilter, search);
  };

  const applyFilters = (status: string, search: string) => {
    let filtered = [...signups];

    // Apply status filter
    if (status) {
      filtered = filtered.filter(signup => signup.status === status);
    }

    // Apply search filter
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(signup => 
        (signup.name && signup.name.toLowerCase().includes(searchLower)) ||
        signup.email.toLowerCase().includes(searchLower)
      );
    }

    setFilteredSignups(filtered);
  };

  const handleSort = (sortBy: string, sortOrder: 'asc' | 'desc') => {
    const sorted = [...filteredSignups].sort((a, b) => {
      let aVal: any = a[sortBy as keyof BetaSignup];
      let bVal: any = b[sortBy as keyof BetaSignup];
      
      if (sortBy === 'timestamp') {
        aVal = aVal ? new Date(aVal as string).getTime() : 0;
        bVal = bVal ? new Date(bVal as string).getTime() : 0;
      } else if (sortBy === 'name') {
        aVal = a.name || '';
        bVal = b.name || '';
      }
      
      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
    
    setFilteredSignups(sorted);
  };

  const handleStatusUpdate = async (signupId: string, newStatus: string) => {
    try {
      await apiService.updateSignupStatus(signupId, newStatus);
      onRefresh();
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status');
    }
  };

  const handleBulkStatusUpdate = async (status: string) => {
    if (selectedSignups.length === 0) {
      alert('Please select signups to update');
      return;
    }

    try {
      await apiService.bulkUpdateStatus({
        signupIds: selectedSignups,
        status
      });
      setSelectedSignups([]);
      onRefresh();
    } catch (error) {
      console.error('Error bulk updating status:', error);
      alert('Failed to update status');
    }
  };

  const handleSelectionChange = (signupId: string, selected: boolean) => {
    if (selected) {
      setSelectedSignups(prev => [...prev, signupId]);
    } else {
      setSelectedSignups(prev => prev.filter(id => id !== signupId));
    }
  };

  const handleSelectAll = (selected: boolean) => {
    if (selected) {
      setSelectedSignups(filteredSignups.map(signup => signup.id));
    } else {
      setSelectedSignups([]);
    }
  };

  const handleExportSelected = () => {
    if (selectedSignups.length === 0) {
      alert('Please select signups to export');
      return;
    }

    // Use simple export with just email and name (like TestFlight)
    ExportService.exportSelectedSimpleCSV(signups, selectedSignups);
  };

  const handleExportAll = () => {
    const hasSelectedFields = Object.values(exportFields).some(field => field);
    if (!hasSelectedFields) {
      alert('Please select at least one field to export');
      return;
    }

    ExportService.exportAllToCSV(signups, exportFields);
  };

  const handleExportAppleConnect = () => {
    ExportService.exportAppleConnectCSV(signups);
  };

  const handleExportFieldChange = (field: keyof ExportField, checked: boolean) => {
    setExportFields(prev => ({
      ...prev,
      [field]: checked
    }));
  };

  return (
    <div id="beta-management">
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">
            <span>👥</span>
            Beta Management Dashboard
          </h3>
          <p className="card-description">View, manage, and export beta tester applications</p>
        </div>

        {/* Quick Stats */}
        <div className="stats-grid mb-6">
          <div className="stat-card">
            <div className="stat-icon">📊</div>
            <div className="stat-number">{signups.length}</div>
            <div className="stat-label">Total Signups</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">⏳</div>
            <div className="stat-number">{signups.filter(s => s.status === 'pending').length}</div>
            <div className="stat-label">Pending</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">✅</div>
            <div className="stat-number">{signups.filter(s => s.status === 'approved').length}</div>
            <div className="stat-label">Approved</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">🚀</div>
            <div className="stat-number">{signups.filter(s => s.status === 'invited').length}</div>
            <div className="stat-label">Invited</div>
          </div>
        </div>
        
        <FilterControls
          onStatusFilter={handleStatusFilter}
          onSort={handleSort}
          onSearch={handleSearch}
          filteredCount={filteredSignups.length}
          totalCount={signups.length}
        />

        {/* Selection Summary */}
        {selectedSignups.length > 0 && (
          <div className="selection-info">
            <h5>📋 Selection Summary</h5>
            <div className="flex items-center gap-4">
              <span>{selectedSignups.length} signup{selectedSignups.length === 1 ? '' : 's'} selected</span>
              <button 
                className="btn btn-secondary" 
                onClick={() => setSelectedSignups([])}
              >
                <span>🗑️</span>
                Clear Selection
              </button>
            </div>
          </div>
        )}

        <BulkActions
          selectedCount={selectedSignups.length}
          onBulkStatusUpdate={handleBulkStatusUpdate}
        />

        <ExportControls
          exportFields={exportFields}
          onExportFieldChange={handleExportFieldChange}
          onExportSelected={handleExportSelected}
          onExportAll={handleExportAll}
          onExportAppleConnect={handleExportAppleConnect}
        />

        <SignupsTable
          signups={filteredSignups}
          selectedSignups={selectedSignups}
          onStatusUpdate={handleStatusUpdate}
          onSelectionChange={handleSelectionChange}
          onSelectAll={handleSelectAll}
        />
      </div>
    </div>
  );
};

export default BetaManagement;
